import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import { fieldsAuto, filterActions, getAuto } from '@entities/Filter';

import { CarBodiesType, MarkType, ModelType, ModificationType, YearCurrentType } from '../types/autoSelectsTypes';
import { useAbTestMobileFilterCookie, variantsType } from '@shared/hooks/useAbTestMobileFilterCookie';
import { ServiceEcommerce2 } from '@services/ServiceEcommerce';

export enum StepCarFilter {
  Mark,
  Model,
  Year,
  Generation,
  Motor,
}

interface useManagerParamsAutoType {
  step: StepCarFilter;
  title: string;
  searchValue: string;
  countSteps: number;
  currentVariant: variantsType;
  handlerSerSearchValue: (value: string) => void;
  handlerStepToBack: () => void;
  handlerSetStep: (value: StepCarFilter) => void;
  handlerSetMark: (value: MarkType) => void;
  handlerSetModel: (value: ModelType) => void;
  handlerSetYear: (value: YearCurrentType) => void;
  handlerSetCarBody: (value: CarBodiesType) => void;
  handlerSetModification: (value: ModificationType) => void;
}

const mapStepTitle = {
  [StepCarFilter.Mark]: 'Выберите марку',
  [StepCarFilter.Model]: 'Выберите модель',
  [StepCarFilter.Year]: 'Выберите год выпуска',
  [StepCarFilter.Generation]: 'Выберите поколение',
  [StepCarFilter.Motor]: 'Выберите модификацию',
};

const generateTitle = (step: StepCarFilter, array: Array<{ name: string; type: StepCarFilter }>) => {
  const str = array
    .filter(x => x.type < step && !!x.name)
    .filter(x => !(step === StepCarFilter.Motor && x.type === StepCarFilter.Year))
    .map(x => x.name)
    .join(' ');
  return mapStepTitle[step] + (str ? ' ' + str + ':' : ':');
};

export const useManagerParamsAuto = (callback?: () => void): useManagerParamsAutoType => {
  const currentVariant = useAbTestMobileFilterCookie();
  const countSteps = 5;
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<number>(StepCarFilter.Mark);
  const [searchValue, setSearchValue] = useState<string>('');
  const {
    mark: markCurrent,
    model: modelCurrent,
    year: yearCurrent,
    modification: modificationCurrent,
    carBody: currentCarBody,
  } = useAppSelector(getAuto);

  const title = generateTitle(step, [
    { type: StepCarFilter.Mark, name: markCurrent?.name || '' },
    { type: StepCarFilter.Model, name: modelCurrent?.name || '' },
    { type: StepCarFilter.Year, name: yearCurrent || '' },
    { type: StepCarFilter.Generation, name: `${currentCarBody?.name || ''} ${currentCarBody?.rangeYear || ''}` || '' },
  ]);

  const mapValue = (value: string): string => {
    return ['все', '—'].includes(value?.toLowerCase()) ? '' : value;
  };

  const handlerSetStep = (value: StepCarFilter) => {
    setStep(value);
  };

  const handlerStepToBack = () => {
    if (step !== StepCarFilter.Mark) {
      setStep(prevState => prevState - 1);
      setSearchValue('');
    }
    if (step === StepCarFilter.Generation && currentVariant === variantsType.B) {
      setStep(StepCarFilter.Model);
    }
  };

  const handlerSerSearchValue = (value: string) => {
    setSearchValue(value);
  };

  const handlerSetMark = useCallback(
    (value: MarkType) => {
      if (markCurrent?.id !== value?.id || step === StepCarFilter.Mark) {
        dispatch(filterActions.setAuto({ key: fieldsAuto.Mark, value }));
        setStep(StepCarFilter.Model);
        setSearchValue('');
        ServiceEcommerce2.abTestFilterAnalytics('marka', value.name);
      }
    },
    [markCurrent, step],
  );

  const handlerSetModel = useCallback(
    (value: ModelType) => {
      if (modelCurrent?.id !== value?.id || step === StepCarFilter.Model) {
        dispatch(filterActions.setAuto({ key: fieldsAuto.Model, value }));
        setStep(currentVariant === variantsType.C ? StepCarFilter.Year : StepCarFilter.Generation);
        setSearchValue('');
        ServiceEcommerce2.abTestFilterAnalytics('model', value.name);
      }
    },
    [modelCurrent, step],
  );

  const handlerSetYear = useCallback(
    (value: YearCurrentType) => {
      if (yearCurrent !== value || step === StepCarFilter.Year) {
        dispatch(filterActions.setAuto({ key: fieldsAuto.Year, value: mapValue(value) }));
        setStep(StepCarFilter.Generation);
        ServiceEcommerce2.abTestFilterAnalytics('year', mapValue(value));
      }
    },
    [yearCurrent, step],
  );

  const handlerSetCarBody = useCallback(
    (value: CarBodiesType) => {
      if (!yearCurrent && currentVariant === variantsType.B) {
        dispatch(filterActions.setAuto({ key: fieldsAuto.Year, value: value.yearF }));
      }
      if (currentCarBody?.name !== value?.name) {
        dispatch(filterActions.setAuto({ key: fieldsAuto.CarBody, value }));
      }
      ServiceEcommerce2.abTestFilterAnalytics('generations', value.name);
      setStep(StepCarFilter.Motor);
    },
    [modelCurrent, yearCurrent, currentVariant],
  );

  const handlerSetModification = useCallback(
    (value: ModificationType) => {
      if (modificationCurrent?.id !== value?.id || (!modificationCurrent.engineName && value.engineName)) {
        dispatch(filterActions.setAuto({ key: fieldsAuto.Modification, value }));
      }
      ServiceEcommerce2.abTestFilterAnalytics('modific', value.engineName);
      callback && callback();
    },
    [modificationCurrent, callback],
  );

  useEffect(() => {
    dispatch(filterActions.resetAuto());
  }, []);

  return {
    title,
    step,
    countSteps,
    searchValue,
    currentVariant,
    handlerSerSearchValue,
    handlerStepToBack,
    handlerSetStep,
    handlerSetMark,
    handlerSetModel,
    handlerSetYear,
    handlerSetCarBody,
    handlerSetModification,
  };
};
